import {
  type ReactNode,
  type ReactElement,
  forwardRef,
  type ComponentPropsWithoutRef,
} from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * HighlightBox - A pure layout container for displaying notifications and informational boxes
 *
 * This component only provides a colored background and border. All content (text, icons, layout)
 * is controlled by the children.
 *
 * For accessibility, add `role="alert"` when displaying important error or warning messages
 * that should be announced to screen readers.
 *
 * @example
 * ```tsx
 * <HighlightBox variant="error" role="alert">
 *   <div className="flex items-start gap-2">
 *     <AlertCircle className="h-4 w-4 text-red-600" />
 *     <div>
 *       <p className="font-semibold">Error</p>
 *       <p className="text-sm">Something went wrong</p>
 *     </div>
 *   </div>
 * </HighlightBox>
 *
 * <HighlightBox variant="success" padding="sm" radius="md">
 *   <p>API Key copied to clipboard</p>
 * </HighlightBox>
 *
 * <HighlightBox variant="info" padding="lg" radius="xl">
 *   <p>More spacious and rounded notification</p>
 * </HighlightBox>
 * ```
 */

const highlightBoxVariants = cva('border transition-colors', {
  variants: {
    variant: {
      error:
        'bg-red-50 dark:bg-red-950/30 border-none text-red-600 dark:text-red-400',
      warning:
        'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
      info: 'bg-blue-50 dark:bg-blue-900/20 border-none',
      success:
        'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400',
      neutral:
        'bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700',
      critical:
        'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
      high: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
      medium:
        'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
      low: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    },
    padding: {
      none: 'p-0',
      sm: 'p-2',
      md: 'p-3',
      lg: 'p-4',
      xl: 'p-6',
    },
    radius: {
      none: 'rounded-none',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      xl: 'rounded-xl',
      '2xl': 'rounded-2xl',
      full: 'rounded-full',
    },
  },
  defaultVariants: {
    variant: 'info',
    padding: 'md',
    radius: 'lg',
  },
});

export interface HighlightBoxProps
  extends
    ComponentPropsWithoutRef<'div'>,
    VariantProps<typeof highlightBoxVariants> {
  /**
   * The content to display inside the highlight box
   */
  children: ReactNode;
}

/**
 * HighlightBox - A simple wrapper that provides background and border styling
 */
export const HighlightBox = forwardRef<HTMLDivElement, HighlightBoxProps>(
  (
    {
      variant = 'info',
      padding = 'md',
      radius = 'lg',
      children,
      className,
      ...props
    },
    ref
  ): ReactElement => {
    return (
      <div
        ref={ref}
        className={cn(
          highlightBoxVariants({ variant, padding, radius }),
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

HighlightBox.displayName = 'HighlightBox';
